import {Logout as LogoutIcon} from '@volkswagen-onehub/icons-core';
import {ProfileCircle as ProfileIcon} from '@volkswagen-onehub/icons-core/dist/ProfileCircle';
import {Settings as SettingsIcon} from '@volkswagen-onehub/icons-core/dist/Settings';
import * as React from 'react';
import {FlyoutLoggedInModel} from '../../../../generated/core';
import {
    LoginFlyoutFooterLink,
    LoginFlyoutUserInfoText
} from '../../../components/LoginFlyout';
import {LoginFlyoutViewV2} from '../../../components/LoginFlyoutV2';
import {useLoginStore} from '../../../context';
import {CyAttributeAppender} from '../../../test/CyAttributeAppender';
import {elseUndefined} from '../../../utils/elseUndefined';
import {getUserName} from './getUserName';
import {
    Text,
    TextTag,
    TokenTextAppearance
} from '@volkswagen-onehub/components-core';
import {CmsTextLink} from '../../../components/links/CmsTextLink';
import {getIcon} from '../../../utils/getMappedIcon';

interface FlyoutLoggedInPublishProps extends FlyoutLoggedInModel {
    readonly heading: React.ReactNode;
    readonly copy: React.ReactNode;
    readonly button: React.ReactNode;
    readonly handleBtnLogoutClick: React.MouseEventHandler<HTMLButtonElement>;
    readonly handleBtnSettingsClick:
        | React.MouseEventHandler<HTMLButtonElement>
        | undefined;
}

export const FlyoutLoggedInPublish: React.FC<
    FlyoutLoggedInPublishProps
> = props => {
    const {
        btnLogoutLabel,
        btnSettingsLabel,
        greeting,
        fallbackUserName,
        heading,
        copy,
        button,
        handleBtnLogoutClick,
        handleBtnSettingsClick
    } = props;

    const loginStore = useLoginStore();
    const {userData, organizationData, isLoggedIn} = loginStore;

    const name: string = getUserName({
        greeting,
        fallbackUserName,
        userData,
        organizationData,
        isLoggedIn
    });
    const email =
        elseUndefined(userData, userData?.email) ??
        elseUndefined(organizationData, organizationData?.email);
    const userInfoLabel = email || name;

    const footerIcons = [
        <CyAttributeAppender key="flyoutFooterLink" name="logout">
            <LoginFlyoutFooterLink
                key="flyoutFooterLink"
                href="javascript:void(0)"
                label={btnLogoutLabel}
                icon={<LogoutIcon aria-hidden />}
                onClick={handleBtnLogoutClick}
                contentId={userData ? 'IDK logout' : 'CIDK logout'}
            />
        </CyAttributeAppender>
    ];

    // use localized profile link if available or the default URL to ID profile app of IDK/CIDK otherwise empty
    const settingsContentId = `login-flyout-${loginStore.customerType}-settings`;
    let settings;
    let userInfoElement;
    if (props.profileLinkAvailable) {
        const icon = props.profileLinkIcon ? (
            getIcon(props.profileLinkIcon, 'default')
        ) : (
            <SettingsIcon aria-hidden />
        );
        settings = (
            <Text appearance={TokenTextAppearance.copy100} tag={TextTag.span}>
                <CmsTextLink
                    icon={icon}
                    href={props.profileLinkHref}
                    aria-label={props.profileLinkTitle}
                    target={props.profileLinkTarget}
                    contentId={settingsContentId}
                >
                    {props.profileLinkLabel}
                </CmsTextLink>
            </Text>
        );
        footerIcons.unshift(settings);
        userInfoElement = (
            <Text appearance={TokenTextAppearance.copy100} tag={TextTag.span}>
                <CmsTextLink
                    icon={<ProfileIcon aria-hidden />}
                    href={props.profileLinkHref}
                    target={props.profileLinkTarget}
                    contentId={settingsContentId}
                >
                    {userInfoLabel}
                </CmsTextLink>
            </Text>
        );
    } else if (handleBtnSettingsClick) {
        settings = (
            <LoginFlyoutFooterLink
                icon={<SettingsIcon aria-hidden />}
                href="javascript:void(0)"
                label={btnSettingsLabel}
                onClick={handleBtnSettingsClick}
                contentId={settingsContentId}
            />
        );
        footerIcons.unshift(settings);
        userInfoElement = (
            <LoginFlyoutFooterLink
                icon={<ProfileIcon aria-hidden />}
                href="javascript:void(0)"
                label={userInfoLabel}
                onClick={handleBtnSettingsClick}
                contentId={settingsContentId}
            />
        );
    } else {
        userInfoElement = (
            <LoginFlyoutUserInfoText
                label={userInfoLabel}
                icon={<ProfileIcon aria-hidden />}
            />
        );
    }

    return (
        <LoginFlyoutViewV2
            heading={heading}
            copy={copy}
            button={button}
            userInfo={userInfoElement}
            greeting={name}
            footerIcons={footerIcons}
        />
    );
};
